import { useEffect, useRef, useState, ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { TrendingUp, ShieldAlert, Cpu } from 'lucide-react';
import { experiences } from '@/lib/data';

const AnimatedCard = ({ children, delay }: { children: ReactNode, delay: number }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.15, rootMargin: "-25% 0px -25% 0px" }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`transition-all duration-700 ease-in-out transform ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-95 pointer-events-none'
        }`}
    >
      {children}
    </div>
  );
};

const ExperienceSection = () => {
  return (
    <section id='experience' className='py-20 lg:py-32 relative'>
      <div className='container mx-auto px-4 lg:px-8'>
        <div className='text-center mb-16'>
          <Badge variant='outline' className='mb-4 border-primary/50 text-primary'>
            Career History
          </Badge>
          <h2 className='text-3xl lg:text-5xl font-bold mb-6 tracking-tight'>
            Building the <span className='text-foreground/70 font-light'>Future of</span> FinTech
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto'>
            A track record of steering complex digital transformations, securing financial systems, and leading high-performing engineering teams globally.
          </p>
        </div>

        {/* Professional Experience */}
        <div className='mb-8 max-w-5xl mx-auto'>
          <div className='space-y-40 lg:space-y-52'>
            {experiences.map((exp, index) => (
              <AnimatedCard key={`${exp.company}-${index}`} delay={index * 150}>
                <Card
                  className='card-elegant border-border/40 hover:border-primary/30 transition-all duration-300 shadow-none hover:shadow-glow hover:-translate-y-1'
                >
                  <CardHeader className='pb-4 border-b border-border/20'>
                    <div className='flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4'>
                      <div>
                        <h4 className='text-2xl font-bold text-foreground tracking-tight'>
                          {exp.role}
                        </h4>
                        <p className='text-lg text-primary/90 font-medium font-mono tracking-tighter mt-1'>
                          {exp.company}
                        </p>
                      </div>
                      <div className='flex flex-col lg:items-end gap-2.5'>
                        <Badge variant='secondary' className='w-fit bg-primary/10 text-primary hover:bg-primary/20 border-0'>
                          {exp.type}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className='pt-6'>
                    <div className='grid md:grid-cols-2 gap-x-8 gap-y-4'>
                      {exp.achievements.map((achievement, idx) => {
                        // Determine the best icon based on the content
                        let Icon = TrendingUp;
                        if (achievement.toLowerCase().includes('compliance') || achievement.toLowerCase().includes('security') || achievement.toLowerCase().includes('aml')) {
                          Icon = ShieldAlert;
                        } else if (achievement.toLowerCase().includes('architecture') || achievement.toLowerCase().includes('microservices') || achievement.toLowerCase().includes('system')) {
                          Icon = Cpu;
                        }

                        return (
                          <div key={idx} className='flex items-start group'>
                            <div className='mt-1 mr-3 p-1 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors'>
                              <Icon className='w-3.5 h-3.5 text-primary' />
                            </div>
                            <span className='text-muted-foreground text-sm leading-relaxed group-hover:text-foreground/90 transition-colors'>
                              {achievement}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
